import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ScoreTabNavigation from './ScoreTabNavigation';
import DynamicScoreContent from './DynamicScoreContent';
import useAdminScoreData from 'utils/hooks/useAdminScoreData';
import {
  formatwebsiteUrlsToArr,
  transformToKeywordFrequencyMap,
  buildGapFromClientFreqV2,
} from 'helpers/FormatKeywordsHelper';
import { renderTextDiffToHtml } from 'helpers/diffHelper';
import { useWordsInBold } from 'utils/contexts/ScoreBoldWordsContext';
import { useSemanticScore } from 'utils/contexts/SemanticScoreContext';
import { getScoringLanguageLabel } from 'helpers/ProjectLanguageHelper';

import './scoring-section.css';

const AdminScoringSectionContainer = (props) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(props.defaultScoreTab || 'global');
  const [isSemanticGapEditing, setIsSemanticGapEditing] = useState(false);
  const [isSemanticGapCooldown, setIsSemanticGapCooldown] = useState(false);
  const seoScoreEnabled = props.seoScoreEnabled !== false;

  const { updateWordsInBold, clearWordsInBold } = useWordsInBold();
  const { updateSemanticScore } = useSemanticScore();

  // Utiliser useRef pour éviter la boucle infinie avec onScoreDataChange
  const onScoreDataChangeRef = useRef(props.onScoreDataChange);

  // Mettre à jour la ref à chaque render
  useEffect(() => {
    onScoreDataChangeRef.current = props.onScoreDataChange;
  });

  useEffect(() => {
    let cooldownTimer;
    if (isSemanticGapEditing) {
      setIsSemanticGapCooldown(true);
      return undefined;
    }

    cooldownTimer = setTimeout(() => {
      setIsSemanticGapCooldown(false);
    }, 5000);

    return () => clearTimeout(cooldownTimer);
  }, [isSemanticGapEditing]);


  const defaultScoreData = {
    globalScore: 0,
    seoScore: 0,
    uxScore: 0,
    industryAverage: 0,
    yourRanking: 0,
    competitors: [],
    keywordTracking: [],
    metrics: {
      semanticResult: {
        score: 0,
        infoTooltip: '',
        data: { wordsInBold: [], suggestions: [] },
      },
      tags: {
        score: 0,
        infoTooltip: '',
        data: {
          title: { score: 0, present: false },
          metaDescription: { score: 0, present: false },
          headings: { score: 0, h1: 0, h2: 0, h3: 0 },
          images: { score: 0, withAlt: 0, withoutAlt: 0 },
        },
      },
      keywordStuffing: {
        title: 'Keyword stuffing',
        score: 0,
        infoTooltip: '',
        data: [],
      },
      internalLinking: {
        score: 0,
        infoTooltip: '',
        data: [],
      },
      readability: {
        score: { score: 0 },
        infoTooltip: '',
      },
      structure: {
        title: 'Structure',
        score: 0,
        infoTooltip: '',
      },
    },
  };

  const buildGapFromList = (semanticGapList) => {
    if (!Array.isArray(semanticGapList) || semanticGapList.length === 0) {
      return null;
    }

    const result = {};

    semanticGapList.forEach((item) => {
      if (!item) return;

      if (typeof item === 'string') {
        const match = item.match(/^(.+?)\((\d+)\)$/);
        if (match) {
          result[match[1].trim()] = parseInt(match[2], 10);
        } else if (item.trim()) {
          result[item.trim()] = 1;
        }
        return;
      }

      // Score API uses { name, count }; internal format uses { word, frequency }.
      const word = (item.word || item.name || item.keyword || '').toString().trim();
      if (!word) return;

      const frequency = Number(item.frequency ?? item.count ?? item.freq ?? item.total ?? 1);
      result[word] = Number.isNaN(frequency) ? 1 : frequency;
    });

    return Object.keys(result).length > 0 ? result : null;
  };

  const buildGapFromScore = (scoreData) => {
    const semanticData = scoreData?.metrics?.semanticResult?.data;
    const fromClientFreq = buildGapFromClientFreqV2(semanticData);
    if (fromClientFreq.length > 0) {
      return buildGapFromList(fromClientFreq);
    }
    return buildGapFromList(semanticData?.semanticGap);
  };

  const analysisPayload = useMemo(() => {
    const decodedText = new DOMParser().parseFromString(
      props.actualTextContent,
      'text/html'
    ).documentElement.textContent;

    const hasValidCombinedKeywords = props.combinedKeywords &&
      props.combinedKeywords.length > 0 &&
      props.combinedKeywords.trim() !== '';

    const hasValidKeywords = props.keywords &&
      typeof props.keywords === 'object' &&
      props.keywords !== null &&
      Object.keys(props.keywords).length > 0 &&
      Object.values(props.keywords).some(value => value && value > 0);

    // Extraire le texte initial (sans HTML) si disponible
    const decodedInitialText = props.initialTextContent
      ? new DOMParser().parseFromString(
        props.initialTextContent,
        'text/html'
      ).documentElement.textContent
      : '';


    const gapTextValue = decodedInitialText && decodedInitialText.trim()
      ? renderTextDiffToHtml(decodedInitialText, decodedText)
      : (props.actualHtmlContent || '');

    const payload = {
      html: props.actualHtmlContent,
      text: decodedText,
      website: props.orderWebsite,
      keyword: props.keyword,
      gap: (() => {
        if (hasValidCombinedKeywords) {
          return transformToKeywordFrequencyMap(props.combinedKeywords);
        }
        if (hasValidKeywords) {
          return props.keywords;
        }

        const gapFromList = buildGapFromList(props.list);
        if (gapFromList && Object.keys(gapFromList).length > 0) {
          return gapFromList;
        }

        const gapFromScore =
          buildGapFromScore(props.initialContentScore)
          || buildGapFromScore(props.preCalculatedScoreData);
        if (gapFromScore && Object.keys(gapFromScore).length > 0) {
          return gapFromScore;
        }

        // Si le gap est vide, ne pas utiliser le keyword comme fallback
        return {};
      })(),
      gap_text: gapTextValue || '',
      initial_text: decodedInitialText,
      language: getScoringLanguageLabel(props.language),
      country: props.country || 'FR',
      max_words: props.maxWords,
      number_words: props.wordCount,
      maillage_interne: formatwebsiteUrlsToArr(props.websites),
    };

    return payload;
  }, [
    props.actualHtmlContent,
    props.actualTextContent,
    props.orderWebsite,
    props.keyword,
    props.keywords,
    props.combinedKeywords,
    props.list,
    props.initialContentScore,
    props.preCalculatedScoreData,
    props.initialTextContent,
    props.language,
    props.country,
    props.maxWords,
    props.wordCount,
    props.websites,
  ]);

  const { scoreData, error } =
    useAdminScoreData(
      props.textId,
      '/analyze/global/gap',
      analysisPayload,
      {
        initialSpinnerDelay: 0,
        analyzeDelay: 4000,
        analyzeSpinnerDelay: 0,
        disableAnalyze: !seoScoreEnabled || isSemanticGapEditing || isSemanticGapCooldown,
      },
      2000,
      props.mode === 'admin' ? '/texts' : '/texts/external'
    );

  // Utiliser useRef pour les fonctions des contextes
  const updateWordsInBoldRef = useRef(updateWordsInBold);
  const clearWordsInBoldRef = useRef(clearWordsInBold);
  const updateSemanticScoreRef = useRef(updateSemanticScore);

  useEffect(() => {
    updateWordsInBoldRef.current = updateWordsInBold;
    clearWordsInBoldRef.current = clearWordsInBold;
    updateSemanticScoreRef.current = updateSemanticScore;
  });

  useEffect(() => {
    if (!seoScoreEnabled) {
      clearWordsInBoldRef.current();
      updateSemanticScoreRef.current(0);
      return;
    }

    if (scoreData && scoreData.metrics && scoreData.metrics.semanticResult) {
      const wordsInBold =
        scoreData.metrics.semanticResult.data?.wordsInBold || [];

      const score = scoreData.metrics.semanticResult.score || 0;

      updateWordsInBoldRef.current(wordsInBold);
      updateSemanticScoreRef.current(score);
    } else if (error) {
      clearWordsInBoldRef.current();
      updateSemanticScoreRef.current(0);
    }
  }, [scoreData, error, seoScoreEnabled]);

  // Utiliser la ref au lieu de la prop directement
  useEffect(() => {
    if (onScoreDataChangeRef.current && scoreData) {
      onScoreDataChangeRef.current(scoreData);
    }
  }, [scoreData]);


  if (!seoScoreEnabled) {
    return (
      <div className="scoring-section-container score-disabled">
        <ScoreTabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
        <DynamicScoreContent
          activeTab={activeTab}
          scoreData={defaultScoreData}
          isEditing={props.isEditing}
          websites={props.websites}
          actualHtmlContent={props.actualHtmlContent}
          setIsEditing={props.setIsEditing}
          handleInputChange={props.handleInputChange}
          checkIfAllLinesGreen={props.checkIfAllLinesGreen}
          userRole={props.userRole}
          onGapUpdate={props.handleGapUpdate}
          isKeywordBoxReadOnly={props.isKeywordBoxReadOnly}
          combinedKeywords={props.combinedKeywords}
          handleKeywordChange={props.handleKeywordChange}
          orderContentType={props.orderContentType}
          autoKeywordsGenerationLoading={props.autoKeywordsGenerationLoading}
          isReadOnly={props.isKeywordBoxReadOnly}
          wordCount={props.wordCount}
          keyword={props.keyword}
          url={props.url}
          orderWebsite={props.orderWebsite}
          language={props.language}
          country={props.country}
          gapAnalysisEnabled={props.gapAnalysisEnabled}
          onGapEditingChange={setIsSemanticGapEditing}
        />
        <div className="score-disabled-overlay">
          <span>
            {t('Votre contenu mérite mieux que du hasard.Activez le scoring pour mesurer, comprendre et améliorer vos performances.')}
            <br />
            👉{' '}
            <Link to="/AdminClient/Forfait" className="score-upgrade-link">
              {t('Passez au plan supérieur')}
            </Link>{' '}
            {t('pour débloquer cette fonctionnalité.')}
          </span>
        </div>
      </div>
    );
  }

  // Vérifier si l'erreur est liée à un texte qui n'existe pas encore (nouveau texte premium)
  const isTextNotExistError = error && (
    (typeof error === 'string' && error.toLowerCase().includes("this text doesn't exist")) ||
    (typeof error === 'string' && error.toLowerCase().includes("texte n'existe pas")) ||
    (error.message && (error.message.toLowerCase().includes("this text doesn't exist") ||
      error.message.toLowerCase().includes("texte n'existe pas")))
  );

  if (error && !isTextNotExistError) {
    return (
      <div className="score-error-container">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Erreur lors du calcul du score:</strong>
          <br />
          {error.message || error || "Une erreur inattendue s'est produite."}
        </div>
      </div>
    );
  }

  // Si l'erreur est "text doesn't exist" ou si on a une erreur générale, afficher les scores par défaut
  if (error && isTextNotExistError) {
    return (
      <div className="scoring-section-container">
        <ScoreTabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
        <DynamicScoreContent
          activeTab={activeTab}
          scoreData={defaultScoreData}
          isEditing={props.isEditing}
          websites={props.websites}
          actualHtmlContent={props.actualHtmlContent}
          setIsEditing={props.setIsEditing}
          handleInputChange={props.handleInputChange}
          checkIfAllLinesGreen={props.checkIfAllLinesGreen}
          userRole={props.userRole}
          onGapUpdate={props.handleGapUpdate}
          isKeywordBoxReadOnly={props.isKeywordBoxReadOnly}
          combinedKeywords={props.combinedKeywords}
          handleKeywordChange={props.handleKeywordChange}
          orderContentType={props.orderContentType}
          autoKeywordsGenerationLoading={props.autoKeywordsGenerationLoading}
          isReadOnly={props.isKeywordBoxReadOnly}
          wordCount={props.wordCount}
          keyword={props.keyword}
          url={props.url}
          orderWebsite={props.orderWebsite}
          language={props.language}
          country={props.country}
          gapAnalysisEnabled={props.gapAnalysisEnabled}
          onGapEditingChange={setIsSemanticGapEditing}
        />
      </div>
    );
  }

  return (
    <div className={`scoring-section-container ${!seoScoreEnabled ? 'score-disabled' : ''}`}>
      <ScoreTabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
      <DynamicScoreContent
        activeTab={activeTab}
        scoreData={
          scoreData && scoreData.metrics
            ? scoreData
            : defaultScoreData
        }
        isEditing={props.isEditing}
        websites={props.websites}
        actualHtmlContent={props.actualHtmlContent}
        setIsEditing={props.setIsEditing}
        handleInputChange={props.handleInputChange}
        checkIfAllLinesGreen={props.checkIfAllLinesGreen}
        userRole={props.userRole}
        onGapUpdate={props.handleGapUpdate}
        isKeywordBoxReadOnly={props.isKeywordBoxReadOnly}
        combinedKeywords={props.combinedKeywords}
        handleKeywordChange={props.handleKeywordChange}
        orderContentType={props.orderContentType}
        autoKeywordsGenerationLoading={props.autoKeywordsGenerationLoading}
        isReadOnly={props.isKeywordBoxReadOnly}
        wordCount={props.wordCount}
        keyword={props.keyword}
        url={props.url}
        orderWebsite={props.orderWebsite}
        language={props.language}
        country={props.country}
        gapAnalysisEnabled={props.gapAnalysisEnabled}
        onGapEditingChange={setIsSemanticGapEditing}
      />
      {!seoScoreEnabled && (
        <div className="score-disabled-overlay">
          <span>
            {t('Votre contenu mérite mieux que du hasard.Activez le scoring pour mesurer, comprendre et améliorer vos performances.')}
            <br />
            👉{' '}
            <Link to="/AdminClient/Forfait" className="score-upgrade-link">
              {t('Passez au plan supérieur')}
            </Link>{' '}
            {t('pour débloquer cette fonctionnalité.')}
          </span>
        </div>
      )}
    </div>
  );
};

export default AdminScoringSectionContainer;

